import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/app/ProtectedRoute";
import { FeatureGate } from "@/components/app/FeatureGate";
import AppShell from "@/components/app/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Pitches from "@/pages/Pitches";
import PitchDetail from "@/pages/PitchDetail";
import MyBookings from "@/pages/MyBookings";
import Fixtures from "@/pages/Fixtures";
import Teams from "@/pages/Teams";
import Leagues from "@/pages/Leagues";
import { ErrorBoundary } from "@/components/app/ErrorBoundary";
import WaitingList from "@/pages/WaitingList";
import TaskBoard from "@/pages/TaskBoard";
import Referees from "@/pages/Referees";
import Coaches from "@/pages/Coaches";
import Documents from "@/pages/Documents";
import PublicDocuments from "@/pages/PublicDocuments";
import KitOrders from "@/pages/KitOrders";
import Players from "@/pages/Players";
import MyRefereeing, { PublicRefereePage } from "@/pages/MyRefereeing";
import Incidents from "@/pages/Incidents";
import FirstAidKits from "@/pages/FirstAidKits";
import ReportBuilder from "@/pages/ReportBuilder";
import FriendlyRequests from "@/pages/FriendlyRequests";
import WhatsNew from "@/pages/WhatsNew";
import AppFeedback from "@/pages/AppFeedback";
import Chat from "@/pages/Chat";
import FAUpdates from "@/pages/FAUpdates";
import AskCommittee from "@/pages/AskCommittee";
import Notices from "@/pages/Notices";
import CommitteeContacts from "@/pages/CommitteeContacts";
import ExpenseReclaim from "@/pages/ExpenseReclaim";
import Committee from "@/pages/Committee";
import FinanceHome from "@/pages/FinanceHome";
import FeesPage from "@/pages/FeesPage";
import FinesPage from "@/pages/FinesPage";
import RefereePayments from "@/pages/RefereePayments";
import SwapBoard from "@/pages/SwapBoard";
import Admin from "@/pages/Admin";
import FixturePoster from "@/pages/FixturePoster";
import SocialPosters from "@/pages/SocialPosters";
import VenueCalendar from "@/pages/VenueCalendar";
import YearGroupBookings from "@/pages/YearGroupBookings";
import ChangePassword from "@/pages/ChangePassword";
import Privacy from "@/pages/Privacy";
import DeletionCancel from "@/pages/DeletionCancel";
import ConsentGate from "@/components/app/ConsentGate";
import "@/App.css";

function Shell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user?.role === "vendor" && !["/app/calendar", "/app/account"].some(p => loc.pathname.startsWith(p))) return <Navigate to="/app/calendar" replace />;
  if (user?.role === "treasurer" && ["/app/calendar", "/app/bookings", "/app/fixtures", "/app/admin", "/app/pitches", "/app/swap-board", "/app/year-group", "/app/teams"].some(p => loc.pathname.startsWith(p))) return <Navigate to="/app/finance" replace />;
  return <AppShell><ConsentGate /><ErrorBoundary key={loc.pathname}>{children}</ErrorBoundary></AppShell>;
}

function SplashDismiss() {
  const { loading } = useAuth();
  useEffect(() => {
    if (loading) return;
    const el = document.getElementById("eyfc-splash");
    if (!el) return;
    el.classList.add("hide");
    const t = setTimeout(() => el.remove(), 400);
    return () => clearTimeout(t);
  }, [loading]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SplashDismiss />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/deletion-cancel" element={<DeletionCancel />} />
            <Route path="/public/documents" element={<PublicDocuments />} />
            <Route path="/ref/:token" element={<PublicRefereePage />} />
            <Route path="/app/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/app/account" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/app/pitches" element={<ProtectedRoute><Shell><Pitches /></Shell></ProtectedRoute>} />
            <Route path="/app/pitches/:id" element={<ProtectedRoute><Shell><PitchDetail /></Shell></ProtectedRoute>} />
            <Route path="/app/calendar" element={<ProtectedRoute><Shell><VenueCalendar /></Shell></ProtectedRoute>} />
            <Route path="/app/bookings" element={<ProtectedRoute><Shell><MyBookings /></Shell></ProtectedRoute>} />
            <Route path="/app/fixtures" element={<ProtectedRoute><Shell><Fixtures /></Shell></ProtectedRoute>} />
            <Route path="/app/swap-board" element={<ProtectedRoute><Shell><SwapBoard /></Shell></ProtectedRoute>} />
            <Route path="/app/year-group" element={<ProtectedRoute><Shell><FeatureGate flag="year_group"><YearGroupBookings /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/teams" element={<ProtectedRoute><Shell><Teams /></Shell></ProtectedRoute>} />
            <Route path="/app/tasks" element={<ProtectedRoute><Shell><TaskBoard /></Shell></ProtectedRoute>} />
            <Route path="/app/waiting-list" element={<ProtectedRoute><Shell><WaitingList /></Shell></ProtectedRoute>} />
            <Route path="/app/leagues" element={<ProtectedRoute><Shell><FeatureGate flag="leagues"><Leagues /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/referees" element={<ProtectedRoute><Shell><FeatureGate flag="referees"><Referees /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/coaches" element={<ProtectedRoute><Shell><FeatureGate flag="coaches"><Coaches /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/documents" element={<ProtectedRoute><Shell><FeatureGate flag="documents"><Documents /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/incidents" element={<ProtectedRoute><Shell><Incidents /></Shell></ProtectedRoute>} />
            <Route path="/app/first-aid" element={<ProtectedRoute><Shell><FirstAidKits /></Shell></ProtectedRoute>} />
            <Route path="/app/reports" element={<ProtectedRoute><Shell><ReportBuilder /></Shell></ProtectedRoute>} />
            <Route path="/app/friendlies" element={<ProtectedRoute><Shell><FriendlyRequests /></Shell></ProtectedRoute>} />
            <Route path="/app/fa-updates" element={<ProtectedRoute><Shell><FAUpdates /></Shell></ProtectedRoute>} />
            <Route path="/app/committee" element={<ProtectedRoute><Shell><AskCommittee /></Shell></ProtectedRoute>} />
            <Route path="/app/notices" element={<ProtectedRoute><Shell><Notices /></Shell></ProtectedRoute>} />
            <Route path="/app/committee-contacts" element={<ProtectedRoute><Shell><CommitteeContacts /></Shell></ProtectedRoute>} />
            <Route path="/app/whats-new" element={<ProtectedRoute><Shell><WhatsNew /></Shell></ProtectedRoute>} />
            <Route path="/app/feedback" element={<ProtectedRoute><Shell><AppFeedback /></Shell></ProtectedRoute>} />
            <Route path="/app/chat" element={<ProtectedRoute><Shell><Chat /></Shell></ProtectedRoute>} />
            <Route path="/app/kit" element={<ProtectedRoute><Shell><FeatureGate flag="kit"><KitOrders /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/players" element={<ProtectedRoute><Shell><Players /></Shell></ProtectedRoute>} />
            <Route path="/app/refereeing" element={<ProtectedRoute><Shell><MyRefereeing /></Shell></ProtectedRoute>} />
            <Route path="/app/expenses" element={<ProtectedRoute><Shell><FeatureGate flag="expenses"><ExpenseReclaim /></FeatureGate></Shell></ProtectedRoute>} />
            <Route path="/app/committee-reports" element={<ProtectedRoute><Shell><Committee /></Shell></ProtectedRoute>} />
            <Route path="/app/finance" element={<ProtectedRoute><Shell><FinanceHome /></Shell></ProtectedRoute>} />
            <Route path="/app/fees" element={<ProtectedRoute><Shell><FeesPage /></Shell></ProtectedRoute>} />
            <Route path="/app/fines" element={<ProtectedRoute><Shell><FinesPage /></Shell></ProtectedRoute>} />
            <Route path="/app/referee-payments" element={<ProtectedRoute><Shell><RefereePayments /></Shell></ProtectedRoute>} />
            <Route path="/app/admin" element={<ProtectedRoute adminOnly><Shell><Admin /></Shell></ProtectedRoute>} />
            <Route path="/app/admin/poster" element={<ProtectedRoute adminOnly><Shell><FixturePoster /></Shell></ProtectedRoute>} />
            <Route path="/app/admin/social-posters" element={<ProtectedRoute adminOnly><Shell><SocialPosters /></Shell></ProtectedRoute>} />
            <Route path="/app/admin/:tab" element={<ProtectedRoute adminOnly><Shell><Admin /></Shell></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}
